/** @flow */

import { takeEvery, put, all, call, select } from "redux-saga/effects";
import http from "@http";
import config from "@config";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  GetReportingRTWDetailsDefaultReports,
  GetReportingRTWDetailsMyReports,
  GetReportingRTWDetailsReport,
  GetRTWDetailsFiltersSchema,
  CreateRTWDetailsReport,
  UpdateRTWDetailsReport,
  DeleteRTWDetailsReport,
  ExportRTWDetailsReport,
  ExportRTWDetailsReportSuccess,
  GetRTWDetailsReportAbsenceRTWInterview,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import * as selectors from "../../selectors/reports/rtw-details";
import { parseSchema } from "@utils/schema";

type ReportRequest = $Exact<{
  clientId: number,
}>;

function* onGetRTWDetailsFiltersSchema({ payload }: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const schema = yield parseSchema("RtwDetailsReportFiltersDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetRTWDetailsFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetRTWDetailsFiltersSchemaError(e.response));
  }
}

function* onGetReportingRTWDetailsDefaultReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/default-reports`,
    );

    yield put(actionCreators.GetReportingRTWDetailsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingRTWDetailsDefaultReportsError(e.response));
  }
}

function* onGetReportingRTWDetailsMyReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/my-saved-reports`,
    );

    yield put(actionCreators.GetReportingRTWDetailsMyReportsSuccess(response.data));
    yield put(actionCreators.ReSetRTWDetailsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetReportingRTWDetailsMyReportsError(e.response));
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: Object,
  noRedirect?: boolean,
  page?: number,
}>;

function* onGetReportingRTWDetailsReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const page = (payload && payload.page) || 1;
    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/rtw-details`,
        payload.report.filters,
        {
          params: {
            page,
            limit: config.results50PerPage,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/charts`,
        payload.report.filters,
      ),
    ]);
    yield all([
      put(actionCreators.GetReportingRTWDetailsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingRTWDetailsChartsSuccess(chartsResponse.data)),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTING_RTW_DETAILS_REPORT", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield put(actionCreators.GetReportingRTWDetailsReportError(e.response));
  }
}

type CreateRTWDetailsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
  name: string,
}>;

export function* onCreateRTWDetailsReport({
  payload,
}: Action<CreateRTWDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns || !payload.name) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
        columns: payload.columns,
      },
    );

    yield all([
      put(actionCreators.CreateRTWDetailsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(
        actionCreators.GetReportingRTWDetailsReport({
          clientId: payload.clientId,
          report: response.data,
          noRedirect: false,
        }),
      ),
      put(actionCreators.GetReportingRTWDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateRTWDetailsReportError(e.response));
  }
}

type UpdateRTWDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateRTWDetailsReport({
  payload,
}: Action<UpdateRTWDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingRTWDetailsReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateRTWDetailsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingRTWDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateRTWDetailsReportError(e.response));
  }
}

type DeleteRTWDetailsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteRTWDetailsReport({
  payload,
}: Action<DeleteRTWDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteRTWDetailsReportSuccess()),
      put(actionCreators.GetReportingRTWDetailsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteRTWDetailsReportError(e.response));
  }
}

export function* onExportRTWDetailsReport({ payload }: Action<*>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const responce = yield http.post(
      `/api/v1/reports/client/${payload.clientId}/reports/rtw-details/export`,
      { filters: payload.filters, columns: payload.columns },
      {
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportRTWDetailsReportSuccess(responce.data));
  } catch (e) {
    yield put(actionCreators.ExportRTWDetailsReportError(e.response));
  }
}

export function* onExportRTWDetailsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "rtw-details-export.csv");
}

type RtwInterviewQuestionAnswersDataRequest = $Exact<{
  clientId: number,
  rtwInterviewData: Object,
}>;

function* onGetRTWDetailsReportAbsenceRTWInterview({
  payload,
}: Action<RtwInterviewQuestionAnswersDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.rtwInterviewData) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/tools/client/${payload.clientId}/tools/task/rtw-interview/${payload.rtwInterviewData.id}`,
    );

    yield put(actionCreators.GetRTWDetailsReportAbsenceRTWInterviewSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetRTWDetailsReportAbsenceRTWInterviewError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetReportingRTWDetailsDefaultReports, onGetReportingRTWDetailsDefaultReports),
    takeEvery(GetReportingRTWDetailsMyReports, onGetReportingRTWDetailsMyReports),
    takeEvery(GetReportingRTWDetailsReport, onGetReportingRTWDetailsReport),
    takeEvery(GetRTWDetailsFiltersSchema, onGetRTWDetailsFiltersSchema),
    takeEvery(CreateRTWDetailsReport, onCreateRTWDetailsReport),
    takeEvery(UpdateRTWDetailsReport, onUpdateRTWDetailsReport),
    takeEvery(DeleteRTWDetailsReport, onDeleteRTWDetailsReport),
    takeEvery(ExportRTWDetailsReport, onExportRTWDetailsReport),
    takeEvery(ExportRTWDetailsReportSuccess, onExportRTWDetailsReportSuccess),
    takeEvery(GetRTWDetailsReportAbsenceRTWInterview, onGetRTWDetailsReportAbsenceRTWInterview),
  ]);
}
